import React, { useState, useRef, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, MinimizedModalStrip, SyutSelect } from 'components/ui';
import type { ActionButton, ViewModeOption } from 'components/modals/common/ModuleHeader';
import notificationService from '../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { userAdminService, roleService, statusService } from 'api/services';
import type { UserAdminDto, CreateUserAdminRequest, UpdateUserAdminRequest, UserAdminQueryParams, StatusDto, UserLookupResult } from 'api/types';

// ============================================================================
// USER FORM MODAL
// ============================================================================

interface UserFormModalProps {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: UserAdminDto | null;
  onClose: () => void;
  onSave: () => void;
  tabId?: string;
  moduleName?: string;
  instanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordUserName?: string) => void;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  mode,
  record,
  onClose,
  onSave,
  tabId,
  moduleName,
  instanceId,
  minimizeRef,
  onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const authMode = process.env.REACT_APP_AUTH_MODE || 'legacy';

  const [formData, setFormData] = useState({
    userName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    statusCode: 'ACTIVE',
    roleId: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const savedSnapshot = useRef<string | null>(null);

  // TenantHub lookup state
  const [lookupStatus, setLookupStatus] = useState<string | null>(null);
  const [lookupData, setLookupData] = useState<UserLookupResult | null>(null);
  const [lookupConfirmOpen, setLookupConfirmOpen] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const isTenantHubMode = authMode === 'tenant-hub';
  const isExistingUser = lookupStatus === 'exists_globally' || lookupStatus === 'exists_in_tenant';

  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);

  // Populate form when opened
  useEffect(() => {
    if (!open) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (mode === 'edit' && record) {
      const fd = {
        userName:    record.userName,
        firstName:   record.firstName || '',
        middleName:  record.middleName || '',
        lastName:    record.lastName || '',
        email:       record.email || '',
        password:    '',
        confirmPassword: '',
        statusCode:  record.statusCode || 'ACTIVE',
        roleId:      record.assignedRoleId ? String(record.assignedRoleId) : '',
      };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else if (mode === 'copy' && record) {
      const fd = {
        userName:    '',
        firstName:   record.firstName || '',
        middleName:  record.middleName || '',
        lastName:    record.lastName || '',
        email:       record.email || '',
        password:    '',
        confirmPassword: '',
        statusCode:  record.statusCode || 'ACTIVE',
        roleId:      record.assignedRoleId ? String(record.assignedRoleId) : '',
      };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else {
      const fd = { userName: '', firstName: '', middleName: '', lastName: '', email: '', password: '', confirmPassword: '', statusCode: 'ACTIVE', roleId: '' };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    }
    setFieldErrors({});
    setLookupStatus(null);
    setLookupData(null);
    setLookupConfirmOpen(false);
    setIsLookingUp(false);
  }, [open, mode, record]);

  // Async status loader
  const loadStatusOptions = useCallback(async (inputValue: string) => {
    const statuses: StatusDto[] = await statusService.getByType('GENERAL');
    const filtered = inputValue
      ? statuses.filter(s => s.statusName.toLowerCase().includes(inputValue.toLowerCase()))
      : statuses;
    return filtered.map(s => ({ value: s.statusCode, label: s.statusName }));
  }, []);

  // Async role loader
  const loadRoleOptions = useCallback(async (inputValue: string) => {
    const res = await roleService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 200 });
    return res.data.map(r => ({ value: r.id as string, label: r.roleName }));
  }, []);

  // TenantHub username lookup on blur (create/copy only)
  const handleUserNameBlur = useCallback(async () => {
    if (!isTenantHubMode || mode === 'edit') return;
    const userName = formDataRef.current.userName.trim();
    if (!userName) return;

    setIsLookingUp(true);
    try {
      const result = await userAdminService.lookupUser(userName);
      if (result.status === 'exists_in_app') {
        setFieldErrors(prev => ({ ...prev, userName: 'Username already exists in this application' }));
      } else if (result.status === 'exists_globally' || result.status === 'exists_in_tenant') {
        setLookupData(result);
        setLookupConfirmOpen(true);
      } else {
        // status === 'new' — clear any previous lookup state
        setLookupStatus('new');
        setLookupData(null);
      }
    } catch {
      // If lookup fails, let the save flow handle validation
    } finally {
      setIsLookingUp(false);
    }
  }, [isTenantHubMode, mode]);

  // Lookup confirm: user clicks "Yes" — populate fields read-only
  const handleLookupConfirm = useCallback(() => {
    if (!lookupData) return;
    setLookupStatus(lookupData.status);
    setFormData(prev => ({
      ...prev,
      firstName:  lookupData.firstName || prev.firstName,
      middleName: lookupData.middleName || prev.middleName,
      lastName:   lookupData.lastName || prev.lastName,
      email:      lookupData.email || prev.email,
      password:   '',
      confirmPassword: '',
    }));
    setLookupConfirmOpen(false);
    setFieldErrors({});
  }, [lookupData]);

  // Lookup confirm: user clicks "No" — clear username
  const handleLookupCancel = useCallback(() => {
    setLookupConfirmOpen(false);
    setLookupData(null);
    setLookupStatus(null);
    setFormData(prev => ({ ...prev, userName: '' }));
  }, []);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentFormData = { ...formDataRef.current };
    const currentSnapshot = savedSnapshot.current || '';
    const currentIsDirty = currentSnapshot ? JSON.stringify(currentFormData) !== currentSnapshot : false;
    const idx = instanceId || '1';
    let title: string;
    if (mode === 'create' || mode === 'copy') {
      const prefix = mode === 'copy' ? 'Copy' : 'Add';
      const fullName = [currentFormData.firstName, currentFormData.middleName, currentFormData.lastName].filter(Boolean).join(' ');
      title = fullName
        ? `${prefix} - ${fullName} (${idx})`
        : currentFormData.userName
        ? `${prefix} - ${currentFormData.userName} (${idx})`
        : `${prefix} New (${idx})`;
    } else {
      title = `Edit - ${record?.userName || currentFormData.userName}`;
    }
    minimize({
      id: tabId
        ? `${tabId}-user-modal-${mode === 'edit' ? record?.id : mode + '-' + instanceId}`
        : 'user-modal',
      tabId: tabId!,
      moduleName: moduleName || 'User Master',
      title,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(currentFormData);
        savedSnapshot.current = currentSnapshot;
        onRestoreModal?.(mode, instanceId, mode !== 'create' ? record?.userName : undefined);
      },
    });
    onClose();
  }, [tabId, instanceId, mode, record, minimize, moduleName, onRestoreModal, onClose]);

  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  const handleClose = useCallback(() => {
    const currentSnapshot = savedSnapshot.current;
    const dirty = currentSnapshot ? JSON.stringify(formDataRef.current) !== currentSnapshot : false;
    if (dirty) {
      setCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  // Step 1: Validate, then show confirm dialog
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.userName.trim()) errors.userName = 'Username is required';
    if (!formData.firstName.trim()) errors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last Name is required';
    if (mode !== 'edit' && !isExistingUser) {
      if (!formData.password.trim())
        errors.password = 'Password is required';
      if (formData.password && formData.password !== formData.confirmPassword)
        errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, authMode, isExistingUser]);

  // Step 2: After user confirms — validate uniqueness + save
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      // Username uniqueness check for create/copy
      // Skip in TenantHub mode (lookup already checked; backend handles duplicates)
      // Skip for existing TenantHub users (already validated by lookup)
      if (mode !== 'edit' && !isExistingUser && !isTenantHubMode) {
        try {
          const available = await userAdminService.isUserNameAvailable(formData.userName.trim());
          if (!available) {
            setSaving(false);
            setFieldErrors({ userName: 'This username already exists. Please use a different username.' });
            return;
          }
        } catch {
          // Let server enforce if API unavailable
        }
      }

      const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
      if (mode === 'edit' && record) {
        const req: UpdateUserAdminRequest = {
          firstName:   formData.firstName,
          middleName:  formData.middleName || undefined,
          lastName:    formData.lastName,
          email:       formData.email || undefined,
          statusCode:  formData.statusCode,
          roleId:      formData.roleId || undefined,
          rowVersion:  record.rowVersion,
        };
        await userAdminService.update(record.id, req);
        notificationService.toast.success(`User "${fullName}" has been updated successfully.`);
      } else {
        const req: CreateUserAdminRequest = {
          userName:    formData.userName.trim(),
          firstName:   formData.firstName,
          middleName:  formData.middleName || undefined,
          lastName:    formData.lastName,
          email:       formData.email || undefined,
          password:    formData.password || undefined,
          statusCode:  formData.statusCode,
          roleId:      formData.roleId || undefined,
        };
        await userAdminService.create(req);
        notificationService.toast.success(`User "${fullName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'An error occurred while saving. Please try again.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, onSave, authMode, isExistingUser]);

  if (!open) return null;

  const title = mode === 'create' ? 'Add User' : mode === 'copy' ? 'Copy User' : `Edit User — ${record?.userName}`;
  const passwordLabel = authMode === 'tenant-hub'
    ? 'Password (required for new users)'
    : 'Password';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Row 1: Username | Email */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">
                Username <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${fieldErrors.userName ? ' form-field-error' : ''}`}
                value={formData.userName}
                onChange={e => {
                  setFormData(p => ({ ...p, userName: e.target.value }));
                  setFieldErrors(prev => { const { userName, ...rest } = prev; return rest; });
                  // Reset lookup state when username changes
                  if (isExistingUser || lookupStatus) {
                    setLookupStatus(null);
                    setLookupData(null);
                  }
                }}
                onBlur={handleUserNameBlur}
                placeholder="e.g., john.doe"
                readOnly={mode === 'edit' || isExistingUser}
                style={mode === 'edit' || isExistingUser ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={100}
                disabled={isLookingUp}
              />
              {isLookingUp && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Looking up username...</div>}
              {fieldErrors.userName && <div className="form-error-message">{fieldErrors.userName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="e.g., john.doe@example.com"
                readOnly={isExistingUser}
                style={isExistingUser ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={200}
              />
            </div>
          </div>

          {/* Row 2: First Name | Middle Name */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${fieldErrors.firstName ? ' form-field-error' : ''}`}
                value={formData.firstName}
                onChange={e => {
                  setFormData(p => ({ ...p, firstName: e.target.value }));
                  setFieldErrors(prev => { const { firstName, ...rest } = prev; return rest; });
                }}
                placeholder="e.g., John"
                readOnly={isExistingUser}
                style={isExistingUser ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={100}
              />
              {fieldErrors.firstName && <div className="form-error-message">{fieldErrors.firstName}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Middle Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.middleName}
                onChange={e => setFormData(p => ({ ...p, middleName: e.target.value }))}
                placeholder="Optional"
                readOnly={isExistingUser}
                style={isExistingUser ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={100}
              />
            </div>
          </div>

          {/* Row 3: Last Name */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${fieldErrors.lastName ? ' form-field-error' : ''}`}
                value={formData.lastName}
                onChange={e => {
                  setFormData(p => ({ ...p, lastName: e.target.value }));
                  setFieldErrors(prev => { const { lastName, ...rest } = prev; return rest; });
                }}
                placeholder="e.g., Doe"
                readOnly={isExistingUser}
                style={isExistingUser ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={100}
              />
              {fieldErrors.lastName && <div className="form-error-message">{fieldErrors.lastName}</div>}
            </div>
          </div>

          {/* Row 4: Password | Confirm Password (create/copy only, hidden for existing TenantHub users) */}
          {mode !== 'edit' && !isExistingUser && (
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">
                  {passwordLabel}{authMode === 'legacy' && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={`form-input${fieldErrors.password ? ' form-field-error' : ''}`}
                  value={formData.password}
                  onChange={e => {
                    setFormData(p => ({ ...p, password: e.target.value }));
                    setFieldErrors(prev => { const { password, ...rest } = prev; return rest; });
                  }}
                  placeholder="Enter password"
                  maxLength={100}
                />
                {fieldErrors.password && <div className="form-error-message">{fieldErrors.password}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Confirm Password{authMode === 'legacy' && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={`form-input${fieldErrors.confirmPassword ? ' form-field-error' : ''}`}
                  value={formData.confirmPassword}
                  onChange={e => {
                    setFormData(p => ({ ...p, confirmPassword: e.target.value }));
                    setFieldErrors(prev => { const { confirmPassword, ...rest } = prev; return rest; });
                  }}
                  placeholder="Re-enter password"
                  maxLength={100}
                />
                {fieldErrors.confirmPassword && <div className="form-error-message">{fieldErrors.confirmPassword}</div>}
              </div>
            </div>
          )}

          {/* Row 5: Role | Status */}
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Role"
                isAsync
                loadOptions={loadRoleOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.roleId || undefined}
                onChange={val => setFormData(p => ({ ...p, roleId: (val as string) || '' }))}
                placeholder="Select role (optional)..."
                isClearable
                menuPortalTarget={document.body}
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label="Status"
                isAsync
                loadOptions={loadStatusOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.statusCode}
                onChange={val => setFormData(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
                placeholder="Select status..."
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving || isLookingUp}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || isLookingUp}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={(() => {
          const fn = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
          return mode === 'edit'
            ? `Save changes to user "${fn}"?`
            : `Create user "${fn}"?`;
        })()}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setCloseConfirmOpen(false); onClose(); }}
        onCancel={() => setCloseConfirmOpen(false)}
        variant="danger"
      />

      <ConfirmDialog
        open={lookupConfirmOpen}
        title="Existing User Found"
        message={
          lookupData?.status === 'exists_in_tenant'
            ? `The user '${formData.userName}' is already associated with the tenant. Do you want to grant application '${lookupData?.appName || 'this application'}' access to this user?`
            : `The user '${formData.userName}' already exists globally in Tenant Hub. Do you want to associate this user with the tenant and grant access to the application '${lookupData?.appName || 'this application'}'?`
        }
        confirmLabel="Yes, Continue"
        cancelLabel="No, Cancel"
        onConfirm={handleLookupConfirm}
        onCancel={handleLookupCancel}
      />
    </>
  );
};

// ============================================================================
// USER VIEW MODAL
// ============================================================================

interface UserViewModalProps {
  open: boolean;
  record: UserAdminDto | null;
  onClose: () => void;
  onEdit?: (record: UserAdminDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (userName: string) => void;
}

const ViewField: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px' }}>
      {value || <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

const UserViewModal: React.FC<UserViewModalProps> = ({
  open,
  record,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    minimize({
      id: `${tabId}-user-view-${record.userName}`,
      title: `View - ${record.userName}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(record.userName),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!open || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">User Details — {record.userName}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <ViewField label="Username" value={record.userName} />
            <ViewField label="Email" value={record.email} />
          </div>
          <div className="form-grid-2">
            <ViewField label="First Name" value={record.firstName} />
            <ViewField label="Middle Name" value={record.middleName} />
          </div>
          <div className="form-grid-2">
            <ViewField label="Last Name" value={record.lastName} />
            <ViewField label="Display Name" value={record.displayName} />
          </div>
          <div className="form-grid-2">
            <ViewField label="Role" value={record.assignedRoleName} />
            <ViewField label="Status" value={record.statusName} />
          </div>
          <div className="form-grid-2">
            <ViewField label="Last Seen" value={record.lastSeenAt ? new Date(record.lastSeenAt).toLocaleString() : null} />
            <ViewField label="Created" value={record.createdAt ? new Date(record.createdAt).toLocaleString() : null} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => onEdit(record)}>Edit</button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// USER MASTER SCREEN
// ============================================================================

interface UserMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type GridRecord = Omit<UserAdminDto, 'id'> & { id: string };

const UserMasterScreen: React.FC<UserMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // Grid state
  const [queryParams, setQueryParams] = useState<UserAdminQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<UserAdminDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // View state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<UserAdminDto | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Load data
  const loadData = useCallback(async (params: UserAdminQueryParams, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await userAdminService.getAll(params);
      setData(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch {
      notificationService.toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(queryParams);
  }, [queryParams, loadData]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const refetch = useCallback(() => loadData(queryParams, true), [loadData, queryParams]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // CRUD handlers
  const handleAdd = useCallback(() => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    createInstanceCounter.current += 1;
    setCreateInstanceId(String(createInstanceCounter.current));
    setSelectedRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback((user: UserAdminDto) => {
    const modalId = `${tabId}-user-modal-${user.id}`;
    if (tabId && isMinimized(modalId)) {
      dismissMinimized(modalId);
      setSelectedRecord(user);
      setFormMode('edit');
      setFormModalOpen(true);
      return;
    }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    setSelectedRecord(user);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized, dismissMinimized]);

  const handleCopy = useCallback((user: UserAdminDto) => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    copyInstanceCounter.current += 1;
    setCopyInstanceId(String(copyInstanceCounter.current));
    setSelectedRecord(user);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleDeleteRequest = useCallback((user: UserAdminDto) => {
    setSelectedRecord(user);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRecord) return;
    setIsDeleting(true);
    try {
      await userAdminService.delete(selectedRecord.id);
      notificationService.toast.success(`User "${selectedRecord.displayName}" has been deleted successfully.`);
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRecord, refetch]);

  // Restore handler for minimize
  const handleRestoreModal = useCallback((
    mode: 'create' | 'edit' | 'copy',
    instanceId?: string,
    userName?: string,
  ) => {
    if (instanceId) {
      if (mode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (userName) {
      const rec = data.find(r => r.userName === userName);
      if (rec) setSelectedRecord(rec);
    }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [data]);

  // View handlers
  const handleView = useCallback((user: UserAdminDto) => {
    const viewId = tabId ? `${tabId}-user-view-${user.userName}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(user);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((userName: string) => {
    const rec = data.find(r => r.userName === userName);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  // Grid columns
  const columns: SyutGridColumn<GridRecord>[] = [
    {
      id: 'userName',
      header: 'Username',
      accessorKey: 'userName',
      size: 140,
      cell: ({ row }) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.userName}</span>
      ),
    },
    {
      id: 'firstName',
      header: 'First Name',
      accessorKey: 'firstName',
      size: 120,
    },
    {
      id: 'middleName',
      header: 'Middle Name',
      accessorKey: 'middleName',
      size: 120,
      cell: ({ row }) => (row.original as unknown as UserAdminDto).middleName || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      id: 'lastName',
      header: 'Last Name',
      accessorKey: 'lastName',
      size: 120,
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      size: 200,
      cell: ({ row }) => row.original.email || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      id: 'assignedRoleName',
      header: 'Role',
      accessorKey: 'assignedRoleName',
      size: 140,
      cell: ({ row }) => row.original.assignedRoleName || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      id: 'statusName',
      header: 'Status',
      accessorKey: 'statusName',
      size: 100,
      cell: ({ row }) => row.original.statusName ? (
        <span
          className="badge"
          style={{ background: row.original.statusColor || 'var(--bg-secondary)', color: 'var(--text-primary)' }}
        >
          {row.original.statusName}
        </span>
      ) : null,
    },
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessorKey: 'lastSeenAt',
      size: 150,
      cell: ({ row }) => {
        const v = row.original.lastSeenAt;
        if (!v) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        try { return new Date(v).toLocaleDateString(); } catch { return v; }
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 70,
      cell: ({ row }) => {
        const user = row.original as UserAdminDto;
        const items: ActionMenuItem[] = [
          { id: 'view',   label: 'View Details', icon: <IconSystem.eye   size={14} />, onClick: () => handleView(user) },
          { id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={14} />, onClick: () => handleEdit(user) },
          { id: 'copy',   label: 'Copy',   icon: <IconSystem.copy  size={14} />, onClick: () => handleCopy(user) },
          { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, onClick: () => handleDeleteRequest(user), variant: 'danger' },
        ];
        return <ActionMenu items={items} />;
      },
    },
  ];

  const gridData = data as unknown as GridRecord[];

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list',     icon: <IconSystem.list     size={16} />, label: 'List View',     tooltip: 'List View' },
    { id: 'grid',     icon: <IconSystem.grid     size={16} />, label: 'Grid View',     tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'Add User', onClick: handleAdd,  tooltip: 'Add New User', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',  onClick: refetch,   tooltip: 'Refresh',      variant: 'icon' },
    { id: 'export',  icon: <IconSystem.download size={18}/>, label: 'Export',   onClick: () => {},  tooltip: 'Export',        variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="User Master"
        description="Manage application users"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search users...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as 'list' | 'grid') }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={gridData}
            columns={columns}
            isLoading={loading}
            manualPagination
            totalCount={totalCount}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyMessage="No users found"
          />
        </div>

      </div>

      <UserFormModal
        open={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        onSave={handleSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        instanceId={formMode === 'copy' ? copyInstanceId : createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
      />

      <UserViewModal
        open={viewModalOpen}
        record={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        onEdit={(record) => { setViewModalOpen(false); handleEdit(record); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete User"
        message={`Are you sure you want to delete user "${selectedRecord?.displayName}" (${selectedRecord?.userName})? This action cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRecord(null); }}
        variant="danger"
      />
    </div>
  );
};

export default UserMasterScreen;
